/**
 * POS API — thin wrappers around ERPNext POS backend endpoints.
 *
 * Uses getERPClient().call() for mode-aware API calls (JWT or session).
 * Plan: 125-pos-react-ui, Epic 01
 */

import { getERPClient } from "@/lib/erp/client";
import type {
  POSOpeningEntry,
  POSOpeningEntryFull,
  POSProfileData,
  POSProfileSummary,
  OpeningBalanceRow,
  POSItem,
  POSCollection,
  POSTaxDetail,
  POSCustomerSummary,
  BarcodeSearchResult,
  POSInvoiceDoc,
  POSSubmittedInvoice,
  POSPastOrder,
  POSInvoiceFull,
  POSClosingEntryDoc,
  POSClosingEntryResult,
  ValidateCouponResult,
  CouponPreviewResult,
  AvailableCoupon,
  POSTemplateVariantsResponse,
} from "../types";

const POS_API = "erpnext.selling.page.point_of_sale.point_of_sale";
// Tenant-safe wrappers that add company filtering (ERPNext originals bypass permissions)
const POS_SAFE = "zaviago_backend.api.pos_tenant_safe";
// Coupon endpoints (Plan 133)
const POS_COUPON = "zaviago_backend.api.pos_coupon";
// Variant endpoints (Plan 134)
const POS_VARIANT = "zaviago_backend.api.pos_variant_api";

export const posApi = {
  /**
   * Check if user has an open POS Opening Entry (open shift).
   * Uses tenant-safe wrapper (frappe.get_list instead of get_all).
   */
  async checkOpeningEntry(user: string): Promise<POSOpeningEntry[]> {
    const client = getERPClient();
    return client.call<POSOpeningEntry[]>(
      `${POS_SAFE}.check_opening_entry`,
      { user },
    );
  },

  /**
   * Create and submit a POS Opening Entry (open shift).
   * G1: balance_details must be JSON string — server calls json.loads().
   * G6: user is set from frappe.session.user server-side, NOT passed.
   */
  async createOpeningVoucher(
    pos_profile: string,
    company: string,
    balance_details: OpeningBalanceRow[],
  ): Promise<POSOpeningEntry> {
    const client = getERPClient();
    return client.call<POSOpeningEntry>(
      `${POS_API}.create_opening_voucher`,
      {
        pos_profile,
        company,
        balance_details: JSON.stringify(balance_details),
      },
    );
  },

  /**
   * Get full POS Profile data including expanded customer groups.
   */
  async getPOSProfileData(pos_profile: string): Promise<POSProfileData> {
    const client = getERPClient();
    return client.call<POSProfileData>(
      `${POS_API}.get_pos_profile_data`,
      { pos_profile },
    );
  },

  /**
   * Get paginated items for POS grid.
   * Uses tenant-safe wrapper that adds company filter from POS Profile.
   * G1: Response is { items: [...] } — caller must extract .items.
   */
  async getItems(params: {
    start: number;
    page_length: number;
    price_list: string;
    item_group: string;
    pos_profile: string;
    search_term?: string;
    collection?: string;
  }): Promise<{ items: POSItem[] }> {
    const client = getERPClient();
    return client.call<{ items: POSItem[] }>(
      `${POS_SAFE}.get_items`,
      params,
    );
  },

  /**
   * Get visible top-level Storefront Collections for POS category bar.
   * Resolves tenant from POS Profile's company server-side.
   * Plan 132, Epic 02.
   */
  async getPOSCollections(
    pos_profile: string,
  ): Promise<{ collections: POSCollection[] }> {
    const client = getERPClient();
    return client.call<{ collections: POSCollection[] }>(
      `${POS_SAFE}.get_pos_collections`,
      { pos_profile },
    );
  },

  /**
   * Get tax template details for computing tax on cart totals.
   */
  async getTaxTemplate(
    templateName: string,
  ): Promise<{ taxes: POSTaxDetail[] }> {
    const client = getERPClient();
    const doc = await client.get<{ taxes: POSTaxDetail[] }>({
      doctype: "Sales Taxes and Charges Template",
      name: templateName,
    });
    return { taxes: doc.taxes || [] };
  },

  /**
   * Search customers for the customer selector.
   */
  async searchCustomers(
    searchTerm: string,
  ): Promise<POSCustomerSummary[]> {
    const client = getERPClient();
    return client.list<POSCustomerSummary>({
      doctype: "Customer",
      filters: {
        customer_name: ["like", `%${searchTerm}%`],
      },
      fields: ["name", "customer_name", "mobile_no"],
      limit_page_length: 10,
    });
  },

  /**
   * Search for item by barcode, serial number, or batch number.
   * Returns partial data (item_code, serial_no, batch_no) — NOT a full POSItem.
   * Caller must follow up with getItems() to get price/stock.
   */
  async searchBarcode(searchValue: string): Promise<BarcodeSearchResult> {
    const client = getERPClient();
    return client.call<BarcodeSearchResult>(
      `${POS_API}.search_for_serial_or_batch_or_barcode_number`,
      { search_value: searchValue },
    );
  },

  /**
   * Save a POS Invoice as draft via frappe.client.save.
   * Returns the saved doc with `name` assigned.
   * G6b: doc is sent as parsed dict, not stringified.
   */
  async savePOSInvoice(doc: POSInvoiceDoc): Promise<POSSubmittedInvoice> {
    const client = getERPClient();
    return client.call<POSSubmittedInvoice>("frappe.client.save", { doc });
  },

  /**
   * Submit a saved POS Invoice via frappe.client.submit.
   * Triggers GL entries, stock updates, loyalty points.
   * G12: Do NOT set docstatus in doc — submit handles it.
   */
  async submitPOSInvoice(
    doc: POSSubmittedInvoice,
  ): Promise<POSSubmittedInvoice> {
    const client = getERPClient();
    return client.call<POSSubmittedInvoice>("frappe.client.submit", { doc });
  },

  // --- Epic 05 methods ---

  /**
   * Get past POS Invoice list. G15: may return duplicates — dedup client-side.
   * G14: No date filter — returns all matching invoices, not just today's.
   */
  async getPastOrders(
    search_term: string,
    status: string,
    limit = 20,
  ): Promise<POSPastOrder[]> {
    const client = getERPClient();
    return client.call<POSPastOrder[]>(
      `${POS_API}.get_past_order_list`,
      { search_term, status, limit },
    );
  },

  /**
   * Get POS Invoices for a shift period (for closing entry).
   * G10: Whitelisted at pos_closing_entry.py. Returns full docs via get_doc.
   * G7: Pass period_start_date as-is — ERPNext Datetime format.
   */
  async getPOSInvoices(
    start: string,
    end: string,
    pos_profile: string,
    user: string,
  ): Promise<POSInvoiceFull[]> {
    const client = getERPClient();
    return client.call<POSInvoiceFull[]>(
      "erpnext.accounts.doctype.pos_closing_entry.pos_closing_entry.get_pos_invoices",
      { start, end, pos_profile, user },
    );
  },

  /**
   * Get full POS Opening Entry doc (with user + balance_details for closing flow).
   * G18: check_opening_entry only returns summary fields.
   */
  async getOpeningEntryDoc(name: string): Promise<POSOpeningEntryFull> {
    const client = getERPClient();
    return client.get<POSOpeningEntryFull>({
      doctype: "POS Opening Entry",
      name,
    });
  },

  /**
   * Get full POS Invoice doc (for order detail expansion + returns).
   */
  async getPOSInvoiceDoc(name: string): Promise<POSInvoiceFull> {
    const client = getERPClient();
    return client.get<POSInvoiceFull>({
      doctype: "POS Invoice",
      name,
    });
  },

  /**
   * Create a return doc from an existing POS Invoice.
   * G16: Returns unsaved doc dict (no name yet). Must save + submit separately.
   * G8: Items and payments are already negated by backend.
   */
  async makeSalesReturn(
    source_name: string,
  ): Promise<Record<string, unknown>> {
    const client = getERPClient();
    return client.call<Record<string, unknown>>(
      "erpnext.accounts.doctype.pos_invoice.pos_invoice.make_sales_return",
      { source_name },
    );
  },

  /**
   * Save a POS Closing Entry as draft.
   */
  async savePOSClosingEntry(
    doc: POSClosingEntryDoc,
  ): Promise<POSClosingEntryResult> {
    const client = getERPClient();
    return client.call<POSClosingEntryResult>("frappe.client.save", { doc });
  },

  /**
   * Submit a saved POS Closing Entry. Triggers consolidation.
   * G6: If >= 10 invoices, consolidation runs async → status becomes "Queued".
   */
  async submitPOSClosingEntry(
    doc: POSClosingEntryResult,
  ): Promise<POSClosingEntryResult> {
    const client = getERPClient();
    return client.call<POSClosingEntryResult>("frappe.client.submit", { doc });
  },

  /**
   * Get POS Profiles available to the current user.
   * Uses ERPNext's pos_profile_query which handles user-assignment matching
   * server-side. Falls back to direct list if the query endpoint is unavailable.
   */
  async getUserPOSProfiles(_user: string): Promise<POSProfileSummary[]> {
    const client = getERPClient();
    // Query POS Profile directly — Frappe permission system filters by Company.
    // Child table "POS Profile User" is not directly readable by non-admin users,
    // so we query the parent DocType and let server-side permissions handle visibility.
    return client.list<POSProfileSummary>({
      doctype: "POS Profile",
      filters: { disabled: 0 },
      fields: ["name", "company", "warehouse"],
    });
  },

  // --- Coupon endpoints (Plan 133, Epic 02) ---

  /** Validate a coupon code — returns discount details or error. */
  async validateCoupon(
    pos_profile: string,
    coupon_code: string,
    customer?: string,
  ): Promise<ValidateCouponResult> {
    const client = getERPClient();
    return client.call<ValidateCouponResult>(
      `${POS_COUPON}.validate_coupon`,
      { pos_profile, coupon_code, ...(customer ? { customer } : {}) },
    );
  },

  /**
   * Preview discount for a cart + coupon without saving.
   * G2: items must be JSON.stringify'd — backend calls json.loads().
   */
  async previewCouponDiscount(
    pos_profile: string,
    coupon_code: string,
    items: { item_code: string; qty: number; rate: number }[],
    customer?: string,
  ): Promise<CouponPreviewResult> {
    const client = getERPClient();
    return client.call<CouponPreviewResult>(
      `${POS_COUPON}.preview_coupon_discount`,
      {
        pos_profile,
        coupon_code,
        items: JSON.stringify(items),
        ...(customer ? { customer } : {}),
      },
    );
  },

  /** List active, non-exhausted coupons for the picker. */
  async getAvailableCoupons(
    pos_profile: string,
  ): Promise<{ coupons: AvailableCoupon[] }> {
    const client = getERPClient();
    return client.call<{ coupons: AvailableCoupon[] }>(
      `${POS_COUPON}.get_available_coupons`,
      { pos_profile },
    );
  },

  // --- Variant endpoints (Plan 134, Epic 03) ---

  /** Fetch variant data for a parent/template item (used by variant selector modal). */
  async getTemplateVariants(
    template_item_code: string,
    pos_profile: string,
  ): Promise<POSTemplateVariantsResponse> {
    const client = getERPClient();
    return client.call<POSTemplateVariantsResponse>(
      `${POS_VARIANT}.get_template_variants_for_pos`,
      { template_item_code, pos_profile },
    );
  },

  /**
   * Get pre-formatted variant attributes display string for a single item.
   * Returns "Red / M" for variants, null for simple/parent items.
   * Plan 134, Epic 04 — used by barcode scan flow.
   */
  async getVariantAttributesDisplay(
    item_code: string,
  ): Promise<string | null> {
    const client = getERPClient();
    return client.call<string | null>(
      `${POS_VARIANT}.get_variant_attributes_display`,
      { item_code },
    );
  },
};
